import { useState, useCallback, useEffect } from "react";
import { FormErrorMessage, Checkbox, Grid, Box, Flex } from "@chakra-ui/react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Button from "components/base/Button";
import FormInput from "components/base/FormInput";
import Trans from "components/base/Trans";
import { useForm } from "react-hook-form";
import { useCookie } from "utils/cookieHelper";

import Text from "components/base/Text";
import Link from "components/base/Link";

import { submitFormV2 } from "utils/formHelper";

const extraData = { interest: "contact" };

const FormContact = ({
  center,
  onSuccess,
  extraValues,
  needAdditionalCheck,
  ...props
}) => {
  const formId = "97b4cf4bc46a";

  const defaultFormValues = {
    email: "",
    name: "",
    message: "",
    privacy_policy: false,
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const cookie = useCookie(props.cookie);
  const {
    reset,
    handleSubmit,
    control,
    errors: formErrors,
    setError: setFromError,
    register,
    formState,
    setValue,
    clearErrors,
  } = useForm({ defaultValues: defaultFormValues });
  const isDisabled = formState.isSubmitting || loading;

  const onFormSuccess = (values, res) => {
    setLoading(false);
    setError(null);
    reset();
    onSuccess(values, res);
  };

  const onError = (e) => {
    if (e === "email" || e.email) {
      setFromError("email", { type: "manual" });
      if (e.suggestion) {
        setSuggestion(e.suggestion);
      }
    } else {
      setError(true);
    }
    setLoading(false);
  };

  const { executeRecaptcha } = useGoogleReCaptcha();

  const clickHandler = useCallback(
    async (data) => {
      if (!executeRecaptcha) {
        return;
      }
      let result;
      try {
        result = await executeRecaptcha();
      } catch (e) {
        onError(e);
        return;
      }
      submitFormV2(
        data,
        { ...extraData, ...extraValues, "g-recaptcha-response": result },
        formId,
        onFormSuccess,
        onError,
        cookie,
      );
    },
    [executeRecaptcha],
  );

  useEffect(() => {
    if (!executeRecaptcha) {
      return;
    }

    const handleReCaptchaVerify = async () => {
      try {
        await executeRecaptcha();
      } catch (_e) {
        // ignore pre-warm failures
      }
    };

    handleReCaptchaVerify();
  }, [executeRecaptcha]);

  return (
    <Box
      as="form"
      display={center ? "flex" : "block"}
      justifyContent="center"
      onSubmit={handleSubmit((data) => {
        setLoading(true);
        clickHandler(data);
      })}
      {...props}
    >
      <Grid
        maxWidth={["100%", null, "600px"]}
        gap={6}
        justifyItems={center ? "center" : "left"}
      >
        <Box>
          <Text type="title_md" color="primary" mb="xxs">
            <Trans id="LBL_CONTACT_TITLE_MSG" />
          </Text>
          <Text type="headline_md" color="white" mb="md">
            <Trans id="LBL_CONTACT_SUBTITLE_MSG" />
          </Text>
        </Box>
        <FormInput
          control={control}
          id="name-contact"
          name="name"
          isInvalid={error || formErrors.name}
          isDisabled={isDisabled}
          isRequired
          rules={{ required: true }}
          formError={formErrors.name}
          label={<Trans id="LBL_FORM_REGISTER_NAME" />}
          errorLabel={<Trans id="LBL_FORM_REGISTER_NAME_ERROR" />}
        />
        <FormInput
          control={control}
          id="email-contact"
          type="email"
          name="email"
          isInvalid={error || formErrors.email}
          isDisabled={isDisabled}
          isRequired
          rules={{ required: true }}
          formError={formErrors.email}
          label={<Trans id="LBL_FORM_REGISTER_EMAIL" />}
          errorLabel={<Trans id="LBL_FORM_REGISTER_EMAIL_ERROR" />}
          suggestion={suggestion}
          onSuggestionClick={() => {
            setValue("email", suggestion);
            setSuggestion(null);
            clearErrors("email");
          }}
        />
        <FormInput
          control={control}
          id="message-contact"
          name="message"
          type="textarea"
          isInvalid={error || formErrors.name}
          isDisabled={isDisabled}
          isRequired
          rules={{ required: true }}
          formError={formErrors.name}
          label={<Trans id="LBL_FORM_REGISTER_MESSAGE" />}
          errorLabel={<Trans id="LBL_FORM_REGISTER_NAME_ERROR" />}
        />
        <FormInput
          isInvalid={error || formErrors.privacy_policy}
          isDisabled={isDisabled}
          id="privacy-policy-register"
          name="privacy_policy"
          type="custom"
          formError={formErrors.privacy_policy}
          errorLabel={<Trans id="LBL_FORM_REGISTER_PRIVACY_POLICY_ERROR" />}
        >
          <Checkbox
            name="privacy_policy"
            id="privacy-policy-register"
            isDisabled={isDisabled}
            colorScheme="green"
            ref={register({ validate: (value) => value === true })}
            color="white"
          >
            <Text type="p_sm" color="white">
              <Trans id="LBL_FORM_REGISTER_PRIVACY_POLICY_MESSAGE" />{" "}
              <Link href="/privacy-policy" fontWeight="bold" target="_blank">
                <Trans id="LBL_FORM_REGISTER_PRIVACY_POLICY" />
              </Link>
              .
            </Text>
          </Checkbox>
        </FormInput>
        {needAdditionalCheck && (
          <FormInput
            isInvalid={error || formErrors.newsletter}
            isDisabled={isDisabled}
            id="newsletter"
            name="newsletter"
            type="custom"
            formError={formErrors.newsletter}
            errorLabel={<Trans id="LBL_FORM_REGISTER_PRIVACY_POLICY_ERROR" />}
          >
            <Checkbox
              id="newsletter"
              name="newsletter"
              alignItems="start"
              isDisabled={isDisabled}
              colorScheme="green"
              ref={register()}
              color="white"
            >
              <Text type="p_sm" color="white">
                <Trans id="LBL_CONFIGURATOR_FINISH_NEWSLETTER" />
              </Text>
            </Checkbox>
          </FormInput>
        )}
        {error && (
          <FormErrorMessage>
            <Trans id="LBL_FORM_CONTACT_CTA" />
          </FormErrorMessage>
        )}
        <Flex justify="flex-end" w="100%">
          <Button
            variant="outline"
            type="submit"
            isLoading={formState.isSubmitting || loading}
          >
            <Trans id="LBL_FORM_CONTACT_CTA" />
          </Button>
        </Flex>
      </Grid>
    </Box>
  );
};

export default FormContact;
